/** What a redacted `token` value is replaced with. */
export const REDACTED_TOKEN = 'redacted'

/**
 * A `token` query value: plain (`?token=`, `&token=`, `;token=`, `#token=`, `&amp;token=`) or
 * percent-encoded inside another URL, once or more (`%3Ftoken%3D`, `%253Ftoken%253D`). The value
 * ends at the next separator, `%` or quote: tokens are base64url, so they never contain one.
 */
const TOKEN_QUERY_VALUE = /((?:[?&;#]|%(?:25)*(?:3F|26|23))token(?:=|%(?:25)*3D))[^&#%;\s"'<>]+/gi

/**
 * A non-empty `token` value in JSON text (`"token":"..."`), also escaped inside a JSON string
 * (`\"token\":\"...\"`), e.g. a rejected object in an error message.
 */
const TOKEN_JSON_VALUE = /(\\?"token\\?"\s*:\s*\\?")[^"\\]+/gi

/** `text` with every `token` value replaced by {@link REDACTED_TOKEN}. */
export function redactTokenQuery(text: string): string {
    return text
        .replace(TOKEN_QUERY_VALUE, `$1${REDACTED_TOKEN}`)
        .replace(TOKEN_JSON_VALUE, (match, prefix: string) =>
            match.slice(prefix.length) === REDACTED_TOKEN ? match : `${prefix}${REDACTED_TOKEN}`
        )
}

/** Whether `text` holds a `token` value that is not redacted yet. */
export function hasTokenValue(text: string): boolean {
    return redactTokenQuery(text) !== text
}

/**
 * A copy of `value` with every string redacted and every non-empty string under a `token` key
 * replaced. Never changes `value`: event context holds live store state. Shared and circular
 * objects stay shared and circular in the copy; anything but plain objects and arrays is kept.
 */
function redactedCopy(value: unknown, copies = new Map<object, unknown>()): unknown {
    if (typeof value === 'string') {
        return redactTokenQuery(value)
    }
    if (!value || typeof value !== 'object') {
        return value
    }
    const seen = copies.get(value)
    if (seen) {
        return seen
    }
    if (Array.isArray(value)) {
        const copy: unknown[] = []
        copies.set(value, copy)
        for (const item of value) copy.push(redactedCopy(item, copies))
        return copy
    }
    const proto = Object.getPrototypeOf(value)
    if (proto !== Object.prototype && proto !== null) {
        return value
    }
    const copy: Record<string, unknown> = {}
    copies.set(value, copy)
    for (const [key, item] of Object.entries(value)) {
        copy[key] =
            key.toLowerCase() === 'token' && typeof item === 'string' && item !== ''
                ? REDACTED_TOKEN
                : redactedCopy(item, copies)
    }
    return copy
}

/**
 * The parts of a RUM event that can carry a token. All but `error.causes` are fields Datadog
 * (browser SDK 6.10) lets `beforeSend` change; changes to any other field are discarded.
 */
type RumEventUrls = {
    type?: string
    view?: {
        url?: string
        referrer?: string
        name?: string
        performance?: { lcp?: { resource_url?: string } }
    }
    resource?: { url?: string }
    error?: {
        message?: string
        stack?: string
        resource?: { url?: string }
        causes?: unknown
    }
    action?: { target?: { name?: string } }
    long_task?: { scripts?: unknown }
    context?: unknown
}

/**
 * Removes `token` values from a Datadog RUM event (TK-13798) and says whether to send it. The
 * invite link `/invite?token=...` is a credential: with it alone this company's removed
 * member rejoins. Every RUM event carries the page URL and referrer, so without this the token
 * would be stored in Datadog. Applies to any `token` value; nothing else in the app uses one.
 *
 * Call it last in `beforeSend`, after any context is added. Redacts `event` in place (the context
 * and long task scripts are replaced by redacted copies), then returns false only when a token is
 * left in `error.causes`, which `beforeSend` cannot change, so that error is dropped.
 */
export function redactDatadogRumEvent(event: RumEventUrls): boolean {
    // Only string fields already on the event; nothing is added.
    const redact = <T extends object>(target: T | undefined, keys: (keyof T)[]) => {
        for (const key of keys) {
            const value = target?.[key]
            if (typeof value === 'string') {
                ;(target as Record<keyof T, string>)[key] = redactTokenQuery(value)
            }
        }
    }
    redact(event.view, ['url', 'referrer', 'name'])
    redact(event.view?.performance?.lcp, ['resource_url'])
    redact(event.resource, ['url'])
    redact(event.error, ['message', 'stack'])
    redact(event.error?.resource, ['url'])
    redact(event.action?.target, ['name'])
    if (event.long_task?.scripts !== undefined) {
        event.long_task.scripts = redactedCopy(event.long_task.scripts)
    }
    if (event.context !== undefined) {
        event.context = redactedCopy(event.context)
    }

    const causes = event.error?.causes
    if (causes === undefined) {
        return true
    }
    // Error causes are plain data from the SDK (messages and stacks), never circular.
    try {
        return !hasTokenValue(JSON.stringify(causes))
    } catch {
        return false
    }
}
